import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDropzone, FileRejection } from 'react-dropzone';
import PublishIcon from '@material-ui/icons/Publish';
import { msgFieldRequired } from '@Helpers/errorMessages';

import styles from './FileUploadDropzone.module.scss';

interface MultiFileDropzoneProps {
  fieldName: string;
  onFilesSelected: (
    files: File[],
    rejectionInfo?: {
      count: number;
      oversizedFiles?: Array<{ name: string; size: number }>;
      tooManyFiles?: boolean;
    },
  ) => void;
  placeholderText?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  maxFiles?: number;
  maxFileSizeInMB?: number;
  currentCount?: number;
}

export const MultiFileDropzone = ({
  fieldName,
  onFilesSelected,
  placeholderText,
  className,
  disabled,
  required,
  maxFiles,
  currentCount,
  maxFileSizeInMB,
}: MultiFileDropzoneProps) => {
  const { t } = useTranslation('common');

  const maxFileSizeInBytes = useMemo(
    () => (maxFileSizeInMB ? maxFileSizeInMB * 1024 * 1024 : undefined),
    [maxFileSizeInMB],
  );

  const remaining = useMemo(() => {
    if (typeof maxFiles === 'number' && typeof currentCount === 'number') {
      return Math.max(0, maxFiles - currentCount);
    }
    return undefined;
  }, [maxFiles, currentCount]);

  const multiple = useMemo(() => {
    // If maxFiles is 1, never allow multiple selection.
    if (maxFiles === 1) return false;

    // If we know how many slots are remaining, base it on that.
    if (remaining !== undefined) return remaining > 1;

    // Otherwise, allow multiple selection.
    return true;
  }, [maxFiles, remaining]);

  const onDrop = React.useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      // Find files that were rejected specifically for being too large.
      const oversizedFiles = rejected.filter((r) =>
        r.errors.some((e) => e.code === 'file-too-large'),
      );

      // Check if selected files were rejected for exceeding the max file count.
      const tooManyFiles = rejected.some((r) => r.errors.some((e) => e.code === 'too-many-files'));

      // Build rejection error info if there are any rejections.
      const rejectionInfo =
        oversizedFiles.length > 0 || tooManyFiles
          ? {
              count: oversizedFiles.length,
              oversizedFiles: tooManyFiles
                ? undefined // Skip generating if too many files as we'll only show the too many files error.
                : oversizedFiles.length > 0
                ? oversizedFiles.map((r) => ({
                    name: r.file.name,
                    size: r.file.size,
                  }))
                : undefined,
              tooManyFiles,
            }
          : undefined;

      onFilesSelected(accepted, rejectionInfo);
    },
    [onFilesSelected],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    // Note our memoization of `multiple` will prevent the user from selecting more than one file if maxFiles is 1.
    // However, it will also limit the number of files the user can select to 1 if only one slot remains.
    multiple,
    disabled,
    maxSize: maxFileSizeInBytes,
    // Setting `maxFiles` to the number of remaining slots will prevent the user from uploading any files if they select more
    // than the remaining slots.
    // You can see this in our `onDrop` callback by looking at the rejected files if they select more than the remaining slots.
    // e.g. {code: 'too-many-files', message: 'Too many files'}
    // See example here: https://react-dropzone.js.org/#section-accepting-specific-number-of-files
    maxFiles: remaining,
  });

  const fileText = useMemo(() => {
    return typeof maxFiles === 'number' ? t('file_upload.max_files', { count: maxFiles }) : '';
  }, [maxFiles, t]);

  const sizeText = useMemo(() => {
    if (!maxFileSizeInMB) return '';
    return t('file_upload.max_size', {
      count: maxFiles || 1,
      maxSize: maxFileSizeInMB,
    });
  }, [maxFiles, maxFileSizeInMB, t]);

  return (
    <div
      className={classnames(styles.inputWrapper, className, disabled && styles.disabled)}
      {...getRootProps()}
    >
      <Controller
        name={fieldName}
        defaultValue={[]}
        render={() => <input name={fieldName} type="hidden" />}
        rules={{ required: required && msgFieldRequired }}
      />
      <input {...getInputProps({ multiple })} />
      <div className={styles.textWrapper}>
        <PublishIcon />
        <div className={styles.text}>
          <span>{placeholderText || t('file_upload.header')}</span>
          {(fileText || sizeText) && (
            <span className={styles.hint}>
              {fileText}
              {fileText && sizeText && ' · '}
              {sizeText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
